import React, { useCallback, useEffect } from 'react';
import type { Theme } from '@atlassian/jira-common-constants/src/theme-preference.tsx';
import { useTheme } from '@atlassian/jira-controllers-global-theme/src/controllers/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { CUSTOM_NAV_BAR_COLORS_FLAG_ID } from './common/constants.tsx';
import { useCustomNavBarColorWarnings } from './controllers/index.tsx';
import CustomNavBarColorsWarningFlag from './ui/index.tsx';
/**
 * Manages the logic for showing a warning about custom navigation bar colors.
 * Determines whether the warning should be displayed based on feature flags, theme settings,
 * and whether the warning has been previously dismissed. Aimed at ensuring users are aware
 * of potential issues with custom color schemes in the navigation bar.
 */
const useCustomNavBarColorsFlag = () => {
	const { hasFlag, showFlag, dismissFlag } = useFlagsService();
	const [{ navigationTheme }] = useTheme();
	const [
		{ flagIsDismissed, sectionMessageIsDismissed, isLoading },
		{ setFlagIsDismissed, initialFetch },
	] = useCustomNavBarColorWarnings();
	useEffect(() => {
		initialFetch();
	}, [initialFetch]);
	const handleDismiss = useCallback(() => {
		dismissFlag(CUSTOM_NAV_BAR_COLORS_FLAG_ID);
		setFlagIsDismissed();
	}, [dismissFlag, setFlagIsDismissed]);
	const shouldShowFlag = useCallback(
		(theme: Theme) =>
			!isLoading &&
			!hasFlag?.(CUSTOM_NAV_BAR_COLORS_FLAG_ID) &&
			(theme === 'dark' || theme === 'auto') &&
			navigationTheme.usesCustomColours &&
			flagIsDismissed === false &&
			sectionMessageIsDismissed === false,
		[
			hasFlag,
			navigationTheme.usesCustomColours,
			isLoading,
			flagIsDismissed,
			sectionMessageIsDismissed,
		],
	);
	const triggerCustomNavBarColorsFlag = useCallback(
		(newTheme: Theme) => {
			if (shouldShowFlag(newTheme)) {
				showFlag({
					id: CUSTOM_NAV_BAR_COLORS_FLAG_ID,
					render: (props: FlagCustomRenderProps) => (
						<CustomNavBarColorsWarningFlag
							{...props}
							onDismiss={handleDismiss}
							analyticsAttributes={{
								backgroundColor: navigationTheme.backgroundColor,
								highlightColor: navigationTheme.highlightColor,
							}}
						/>
					),
				});
			}
		},
		[shouldShowFlag, handleDismiss, showFlag, navigationTheme],
	);
	return {
		triggerCustomNavBarColorsFlag,
	};
};
export default useCustomNavBarColorsFlag;
